/**
 * The wolf — a quadruped, expressed in the same schema, with no code changes.
 *
 * This plan exists in M1 as a proof rather than as a creature: M2-05 is where
 * wolves actually hunt. What it proves is that the schema survives a body that
 * is not a man's — four legs pooling into one `mobility`, no hands at all —
 * because if it does not, the discovery belongs here and not in the milestone
 * that has creatures to ship.
 *
 * Two things fall out worth naming:
 *
 * - **A ruined foreleg limps but fights.** One paw destroyed costs 0.1 of
 *   pooled mobility; one whole foreleg costs 0.25. Compare a man, who loses
 *   half his mobility with one leg. Four legs are a redundancy, and the model
 *   gets that right for free because the shares are data.
 * - **No hands means no grip mechanic, not zero grip.** `gripL`/`gripR` are
 *   undeclared here, and `deriveCapabilities` reports 1 for a capability no
 *   part contributes to. A wolf is not a man with two severed arms.
 */
export const WOLF_PLAN = {
    id: 'wolf',
    name: 'wolf',
    parts: [
        {
            id: 'head',
            zone: 'head',
            side: null,
            name: 'head',
            hitWeight: 1,
            contributes: { consciousness: 0.75 },
            contains: ['skull', 'brain', 'jaw'],
            vital: true,
        },
        {
            id: 'eye.left',
            zone: 'eye',
            side: 'left',
            name: 'left eye',
            hitWeight: 1,
            contributes: { sight: 0.5 },
            contains: ['eye'],
        },
        {
            id: 'eye.right',
            zone: 'eye',
            side: 'right',
            name: 'right eye',
            hitWeight: 1,
            contributes: { sight: 0.5 },
            contains: ['eye'],
        },
        {
            id: 'neck',
            zone: 'neck',
            side: null,
            name: 'throat',
            hitWeight: 1,
            contributes: { consciousness: 0.25, breath: 0.2 },
            contains: ['artery', 'windpipe', 'spine'],
            vital: true,
        },
        {
            id: 'chest',
            zone: 'chest',
            side: null,
            name: 'chest',
            hitWeight: 1,
            contributes: { breath: 0.6 },
            contains: ['rib', 'lung', 'heart'],
            vital: true,
        },
        {
            id: 'abdomen',
            zone: 'abdomen',
            side: null,
            name: 'flank',
            hitWeight: 1,
            contributes: { breath: 0.2 },
            contains: ['gut', 'liver'],
            vital: true,
        },
        // The forelegs carry the weight and take the blows of anything the wolf
        // closes with, so they are struck more often than the hind — the sub-roll
        // weighting is where a creature's stance lives.
        {
            id: 'foreleg.left',
            zone: 'leg',
            side: 'left',
            name: 'near foreleg',
            hitWeight: 1.5,
            contributes: { mobility: 0.15 },
            contains: ['bone', 'muscle', 'joint'],
        },
        {
            id: 'foreleg.right',
            zone: 'leg',
            side: 'right',
            name: 'off foreleg',
            hitWeight: 1.5,
            contributes: { mobility: 0.15 },
            contains: ['bone', 'muscle', 'joint'],
        },
        {
            id: 'hindleg.left',
            zone: 'leg',
            side: 'left',
            name: 'near hindleg',
            hitWeight: 1,
            contributes: { mobility: 0.15 },
            contains: ['bone', 'muscle', 'joint'],
        },
        {
            id: 'hindleg.right',
            zone: 'leg',
            side: 'right',
            name: 'off hindleg',
            hitWeight: 1,
            contributes: { mobility: 0.15 },
            contains: ['bone', 'muscle', 'joint'],
        },
        {
            id: 'paw.fore.left',
            zone: 'foot',
            side: 'left',
            name: 'near forepaw',
            hitWeight: 1.5,
            contributes: { mobility: 0.1 },
            contains: ['bone', 'tendon'],
        },
        {
            id: 'paw.fore.right',
            zone: 'foot',
            side: 'right',
            name: 'off forepaw',
            hitWeight: 1.5,
            contributes: { mobility: 0.1 },
            contains: ['bone', 'tendon'],
        },
        {
            id: 'paw.hind.left',
            zone: 'foot',
            side: 'left',
            name: 'near hindpaw',
            hitWeight: 1,
            contributes: { mobility: 0.1 },
            contains: ['bone', 'tendon'],
        },
        {
            id: 'paw.hind.right',
            zone: 'foot',
            side: 'right',
            name: 'off hindpaw',
            hitWeight: 1,
            contributes: { mobility: 0.1 },
            contains: ['bone', 'tendon'],
        },
    ],
};
//# sourceMappingURL=wolf.js.map